import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactNode, useRef } from "react";
import { cn } from "../lib/cn.ts";

export interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  dismissible?: boolean;
}

const SIZES = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
} as const;

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
  size = "md",
  dismissible = true,
}: ModalProps) {
  const returnFocus = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next: boolean) => !next && dismissible && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dd-modal-overlay fixed inset-0 z-[100] bg-foreground/50 backdrop-blur-sm" />
        <Dialog.Content
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            const content = event.currentTarget as HTMLElement;
            returnFocus.current =
              document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
            const firstField = content.querySelector<HTMLElement>(
              ".dd-modal-body input:not(:disabled), .dd-modal-body select:not(:disabled), .dd-modal-body textarea:not(:disabled), .dd-modal-body [contenteditable='true'], .dd-modal-body button:not(:disabled), .dd-modal-body [href]",
            );
            if (!firstField) return;
            event.preventDefault();
            firstField.focus();
          }}
          onCloseAutoFocus={(event) => {
            if (!returnFocus.current?.isConnected) return;
            event.preventDefault();
            returnFocus.current.focus();
            returnFocus.current = null;
          }}
          className={cn(
            "dd-modal-content fixed bottom-0 left-1/2 z-[101] flex max-h-[92dvh] w-full -translate-x-1/2 flex-col overflow-visible border border-border bg-card shadow-2xl",
            "rounded-t-2xl sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
            SIZES[size],
          )}
        >
          <div className="flex shrink-0 items-start gap-4 rounded-t-2xl border-b border-border bg-card px-5 py-4 sm:px-5">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-base font-semibold tracking-tight text-foreground">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {dismissible && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Cerrar"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </Dialog.Close>
            )}
          </div>
          <div className="dd-modal-body min-h-0 overflow-y-auto overscroll-contain rounded-b-2xl bg-card p-5">
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
